import React from "react";
import { Link } from "react-router-dom";
import LicensedSponsorBadge from "./LicensedSponsorBadge";

const STORAGE_KEYS = {
  saved: "jobzam_saved_jobs",
  applied: "jobzam_applied_jobs",
  cvPreview: "jobzam_jobs_cv_preview",
  recommendations: "jobzam_jobs_recommendations",
};

const badgeStyles = {
  high: "border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 shadow-sm",
  strong: "border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 shadow-sm",
  recommended: "border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 shadow-sm",
  new: "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 shadow-sm",
  neutral: "border-slate-200 bg-white text-slate-700 shadow-sm",
};

const sourceStyles = {
  LinkedIn: "border-blue-200 bg-gradient-to-r from-blue-50 to-sky-50 text-blue-700 shadow-sm",
  Indeed: "border-cyan-200 bg-gradient-to-r from-cyan-50 to-sky-50 text-cyan-700 shadow-sm",
  "Company Website": "border-slate-200 bg-slate-100 text-slate-700 shadow-sm",
  "Jobs by Reed": "border-sky-200 bg-gradient-to-r from-sky-50 to-blue-100 text-blue-700 shadow-sm",
  "Jobs by Adzuna": "border-violet-200 bg-gradient-to-r from-violet-50 to-purple-100 text-violet-700 shadow-sm",
  "Demo fallback": "border-amber-200 bg-amber-50 text-amber-700 shadow-sm",
};

const keywordStopwords = new Set([
  "about",
  "across",
  "after",
  "against",
  "also",
  "and",
  "are",
  "based",
  "built",
  "can",
  "candidate",
  "company",
  "for",
  "from",
  "have",
  "into",
  "job",
  "jobs",
  "live",
  "match",
  "matched",
  "opportunity",
  "our",
  "role",
  "roles",
  "search",
  "that",
  "the",
  "their",
  "this",
  "using",
  "will",
  "with",
  "work",
  "working",
  "your",
]);

function normalizeKeyword(keyword) {
  return String(keyword || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[^\w]+|[^\w]+$/g, "");
}

export function isDisplayableKeyword(keyword) {
  const original = String(keyword || "").trim();
  const cleaned = normalizeKeyword(original);
  const compact = cleaned.replace(/\s+/g, "");
  const lower = cleaned.toLowerCase();

  if (!cleaned || cleaned.length < 3) return false;
  if (/[#£$%,/@]/.test(original)) return false;
  if (!/[a-z]/i.test(cleaned)) return false;
  if (/^\W+$/.test(cleaned)) return false;
  if (/^\d+$/.test(compact)) return false;
  if (/^\d+k$/i.test(compact)) return false;
  if (/^\d{4,}$/.test(compact)) return false;
  if (/^[a-z]{1,2}\d{1,2}[a-z]{0,2}$/i.test(compact)) return false;
  if (/^[a-z]{1,2}\d[a-z\d]{1,4}$/i.test(compact)) return false;
  if (/^\d+[a-z]+\d*$/i.test(compact)) return false;
  if ((compact.match(/\d/g) || []).length >= compact.length / 2) return false;
  if (keywordStopwords.has(lower)) return false;

  return true;
}

export function cleanKeywordList(keywords, limit) {
  const seen = new Set();
  const clean = [];

  (keywords || []).forEach((keyword) => {
    const normalized = normalizeKeyword(keyword);
    const key = normalized.toLowerCase();

    if (!isDisplayableKeyword(normalized) || seen.has(key)) return;

    seen.add(key);
    clean.push(normalized);
  });

  return typeof limit === "number" ? clean.slice(0, limit) : clean;
}

const salaryFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

export function formatSalary(min, max) {
  if (!min && !max) return "Salary not listed";
  if (min && max) return `${salaryFormatter.format(min)} - ${salaryFormatter.format(max)}`;
  if (min) return `From ${salaryFormatter.format(min)}`;
  return `Up to ${salaryFormatter.format(max)}`;
}

export function getStoredJobIds(type) {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS[type]);
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function setStoredJobIds(type, ids) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(STORAGE_KEYS[type], JSON.stringify(ids));
  } catch {
    return;
  }
}

export function toggleStoredJob(type, jobId) {
  const current = getStoredJobIds(type);
  const next = current.includes(jobId)
    ? current.filter((id) => id !== jobId)
    : [...current, jobId];
  setStoredJobIds(type, next);
  return next;
}

export function addStoredJob(type, jobId) {
  const current = getStoredJobIds(type);
  const next = current.includes(jobId) ? current : [...current, jobId];
  setStoredJobIds(type, next);
  return next;
}

export function removeStoredJob(type, jobId) {
  const next = getStoredJobIds(type).filter((id) => id !== jobId);
  setStoredJobIds(type, next);
  return next;
}

export function getCvPreview() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEYS.cvPreview);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setCvPreview(file) {
  const preview = {
    name: file.name,
    type: file.name.split(".").pop()?.toUpperCase() || "CV",
    createdAt: new Date().toISOString(),
  };

  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(STORAGE_KEYS.cvPreview, JSON.stringify(preview));
    } catch {
      return preview;
    }
  }

  return preview;
}

export function getStoredRecommendations() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEYS.recommendations);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredRecommendations(payload) {
  if (typeof window === "undefined") return payload;

  try {
    window.sessionStorage.setItem(STORAGE_KEYS.recommendations, JSON.stringify(payload));
  } catch {
    return payload;
  }

  return payload;
}

export function getFitLabel(score) {
  if (score >= 90) return "High match";
  if (score >= 82) return "Strong fit";
  return "Recommended";
}

export function JobBadge({ children, tone = "neutral" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
        badgeStyles[tone] || badgeStyles.neutral
      }`}
    >
      {children}
    </span>
  );
}

export function MatchRing({ score, size = "md" }) {
  const dimensions = size === "lg" ? "h-24 w-24 text-2xl" : "h-16 w-16 text-lg";
  const stroke = score >= 90 ? "text-emerald-500" : score >= 82 ? "text-indigo-500" : "text-sky-500";

  return (
    <div className={`relative grid ${dimensions} place-items-center rounded-full bg-slate-100`}>
      <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 100 100">
        <circle
          className="text-slate-200"
          cx="50"
          cy="50"
          r="42"
          fill="none"
          stroke="currentColor"
          strokeWidth="10"
        />
        <circle
          className={stroke}
          cx="50"
          cy="50"
          r="42"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="10"
          strokeDasharray={`${Math.max(score, 1) * 2.64} 264`}
        />
      </svg>
      <span className="relative font-bold text-slate-950">{score}%</span>
    </div>
  );
}

export function SkillPill({ children, tone = "matched" }) {
  return (
    <span
      className={
        "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm " +
        (tone === "missing"
          ? "border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700"
          : "border-emerald-200/80 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700")
      }
    >
      {children}
    </span>
  );
}

export function JobCard({
  job,
  saved,
  applied,
  onSave,
  onApply,
  onTailor,
  compact = false,
  applyLabel,
  tailorLabel,
  saveLabel,
  showMatchDetails = true,
}) {
  const score = job.matchScore ?? job.match_score ?? 0;
  const source =
    job.source ||
    job.source_label ||
    (job.provider === "reed" ? "Jobs by Reed" : job.provider === "adzuna" ? "Jobs by Adzuna" : "Job source");
  const skillsMatched = cleanKeywordList(job.skillsMatched || job.matched_skills || [], compact ? 3 : 5);
  const missingKeywords = cleanKeywordList(job.missingKeywords || job.missing_keywords || [], compact ? 2 : 4);
  const salaryText = job.salary_display || formatSalary(job.salaryMin, job.salaryMax);
  const summary = job.summary || job.description || job.match_summary || "";
  const fitLabel = getFitLabel(score);

  return (
    <article className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-[0_12px_40px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_55px_rgba(15,23,42,0.10)]">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {showMatchDetails ? (
              <JobBadge tone={score >= 90 ? "high" : "strong"}>{fitLabel}</JobBadge>
            ) : null}
            {job.isNew ? <JobBadge tone="new">New role</JobBadge> : null}
            {job.isRecent ? <JobBadge tone="recommended">Recently added</JobBadge> : null}
            <span
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                sourceStyles[source] || "bg-slate-50 text-slate-700 border-slate-200"
              }`}
            >
              {source}
            </span>
            {job.company ? (
              <LicensedSponsorBadge
                companyName={job.company}
                compact
                status={job.licensedSponsorStatus}
              />
            ) : null}
          </div>

          {job.redirect_url ? (
            <a
              href={job.redirect_url}
              target="_blank"
              rel="noreferrer"
              className="mt-3 block"
            >
              <h3 className="text-xl font-bold leading-tight text-slate-950 hover:text-indigo-700">
                {job.title}
              </h3>
            </a>
          ) : (
            <Link to={`/jobs/${job.id}`} className="mt-3 block">
              <h3 className="text-xl font-bold leading-tight text-slate-950 hover:text-indigo-700">
                {job.title}
              </h3>
            </Link>
          )}

          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{job.company}</span>
            <span>{job.location}</span>
            {job.workMode ? <span>{job.workMode}</span> : null}
            {job.experienceLevel ? <span>{job.experienceLevel}</span> : null}
          </div>

          <p className="mt-3 text-sm font-semibold text-slate-800">
            {salaryText || "Salary not listed"}
          </p>

          {!compact ? (
            <p className="mt-3 text-sm leading-6 text-slate-600">{summary}</p>
          ) : null}

          {showMatchDetails ? (
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  SKILLS MATCHED
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {skillsMatched.map((skill) => (
                    <SkillPill key={skill}>{skill}</SkillPill>
                  ))}
                  {!skillsMatched.length ? <span className="text-sm text-slate-500">No exact keyword overlap listed</span> : null}
                </div>
              </div>

              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  MISSING KEYWORDS
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {missingKeywords.map((skill) => (
                    <SkillPill key={skill} tone="missing">
                      {skill}
                    </SkillPill>
                  ))}
                  {!missingKeywords.length ? <span className="text-sm italic text-slate-500">No major keyword gaps found.</span> : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col gap-3 lg:w-40 lg:items-stretch">
          {showMatchDetails ? (
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-3 lg:flex-col lg:justify-center lg:gap-2">
              <MatchRing score={score} />
              <span className="text-center text-sm font-semibold text-slate-700">
                AI match score
              </span>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => onApply(job)}
            className="rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-500/20 transition hover:-translate-y-0.5 hover:shadow-xl"
          >
            {applyLabel || (applied ? "Applied" : "Mark Applied")}
          </button>

          {onTailor ? (
            <button
              type="button"
              onClick={() => onTailor(job)}
              aria-disabled="true"
              className="cursor-not-allowed rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-600 opacity-80"
            >
              {tailorLabel || "Tailor CV coming soon"}
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => onSave(job.id)}
            className={
              "text-sm font-bold transition " +
              (saved
                ? "rounded-2xl bg-slate-900 px-5 py-3 text-white hover:bg-slate-800"
                : "rounded-2xl bg-slate-100 px-5 py-3 text-slate-700 hover:bg-slate-200")
            }
          >
            {saveLabel || (saved ? "Saved" : "Save Job")}
          </button>
        </div>
      </div>
    </article>
  );
}

export function SummaryCard({ label, value, detail }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-medium text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
      <p className="mt-1 text-sm text-slate-600">{detail}</p>
    </div>
  );
}
